// Menu du tableau de bord combiné (offres Solo et Pro) : les onglets des tableaux de bord auxquels le compte a droit,
// réunis en rubriques, sans doublon. Chaque entrée renvoie vers l'onglet d'origine (espace + identifiant d'onglet).
import {
  LayoutDashboard, LineChart, ClipboardCheck, Building2, Users, UserCog, Home, Target, History, BarChart3, CalendarDays,
  FileSignature, BookUser, Megaphone, MessageCircle, CreditCard, DatabaseBackup, Settings, Wallet, Landmark, Scale, Banknote,
  AlertTriangle, Percent, HandCoins, ShieldCheck, PiggyBank, Receipt, Lock, BookOpen, Library, Calculator, FileText, Briefcase,
  Wrench, Hammer, ClipboardList, Repeat, UserPlus, FileSpreadsheet, ArrowRightLeft, LogOut as LogOutIcon, RefreshCcw,
  Eye, MapPin, Contact, Camera, Link2, Bell, Truck, Clock, KeyRound, Store
} from 'lucide-react';

/** Espaces d'origine : rôle requis, rubrique et onglets (identifiants des tableaux de bord existants). */
export const SPACES = {
  agency_director: {
    section: 'Pilotage',
    // Mêmes onglets et libellés que le tableau de bord directeur de l'offre Agence
    tabs: [
      ['overview', 'Vue d’ensemble', LayoutDashboard], ['management', 'À valider', ClipboardCheck],
      ['financial-analytics', 'Pilotage', LineChart], ['accounting', 'Comptabilité (synthèse)', Calculator], ['reports', 'Rapports', FileSpreadsheet],
      ['patrimoine', 'Immeubles', Building2], ['works-tracking', 'Suivi des travaux', Hammer], ['owners', 'Propriétaires', UserCog], ['tenants', 'Locataires', Users],
      ['insights', 'Objectifs', Target], ['activity', 'Journal', History], ['users', 'Utilisateurs', KeyRound],
      ['agenda', 'Agenda', CalendarDays], ['documents-desk', 'Documents', FileSignature],
      ['advertisements', 'Annonces', Megaphone], ['messages', 'Messages', MessageCircle],
      ['subscription', 'Abonnement', CreditCard], ['backup', 'Sauvegarde', DatabaseBackup], ['reset', 'Réinitialisation', AlertTriangle], ['marketplace', 'Marketplace', Store], ['settings', 'Profil', Settings]
    ]
  },
  admin: {
    section: 'Gestion locative',
    tabs: [
      ['overview', 'Suivi administratif', Eye], ['new-client', 'Nouveau client', UserPlus], ['lease-contract', 'Baux', FileText],
      ['renewals', 'Renouvellements & dossiers', RefreshCcw], ['demand-mutation', 'Mutations', ArrowRightLeft],
      ['termination', 'Résiliations', LogOutIcon], ['history', 'Historique administratif', History], ['reports', 'Rapports administratifs', FileSpreadsheet],
      ['agenda', 'Agenda', CalendarDays], ['documents-desk', 'Documents & signatures', FileSignature], ['address-book', 'Carnet d’adresses', BookUser],
      ['advertisements', 'Annonces', Megaphone], ['chat', 'Messages', MessageCircle], ['marketplace', 'Marketplace', Store], ['settings', 'Profil', Settings]
    ]
  },
  accounting: {
    section: 'Comptabilité',
    tabs: [
      ['overview', 'À traiter', ClipboardList], ['payments', 'Loyers & encaissements', Banknote], ['unpaid-rents', 'Impayés', AlertTriangle],
      ['recovery', 'Recouvrement', Scale], ['statement-reconciliation', 'Rapprochement', Repeat], ['cash-session', 'Caisse du jour', Wallet],
      ['account-balances', 'Trésorerie', Landmark], ['penalties', 'Pénalités', Percent], ['owner-payments', 'Reversements', HandCoins],
      ['deposit-refunds', 'Cautions', ShieldCheck], ['tenant-advances', 'Avances', PiggyBank], ['expenses', 'Dépenses', Receipt],
      ['tenant-management', 'Fiches locataires (comptes)', Users], ['owner-statements', 'Arrêtés propriétaires', FileText],
      ['general-journal', 'Journal comptable', BookOpen], ['general-ledger', 'Grand livre', Library], ['trial-balance', 'Balance', Scale],
      ['accounting-periods', 'Clôtures & exports', Lock], ['transaction-history', 'Transactions', Clock], ['states-taxes', 'Journal de recouvrement', FileSpreadsheet],
      ['employees', 'Employés', Users], ['payroll', 'Paie', Calculator], ['reports', 'Rapports comptables', FileSpreadsheet],
      ['agenda', 'Agenda', CalendarDays], ['advertisements', 'Annonces', Megaphone], ['messages', 'Messages', MessageCircle], ['marketplace', 'Marketplace', Store], ['settings', 'Profil', Settings]
    ]
  },
  salesmanager: {
    section: 'Commercial',
    tabs: [
      ['overview', 'Pilotage commercial', LayoutDashboard], ['visits', 'Visites', MapPin],
      ['requests', 'Demandes', ClipboardList], ['occupancy', 'Occupation', Home], ['sales-tracking', 'Ventes', Briefcase],
      ['mandates', 'Mandats', FileSignature],
      ['clients', 'Fiches clients', Users], ['contacts', 'Contacts', Contact], ['property-management', 'Biens à louer', Building2],
      ['property-photos', 'Photos des biens', Camera], ['link-fast', 'Affectation', Link2], ['unpaid', 'Impayés à relancer', Bell], ['alerts', 'Alertes', Bell],
      ['agenda', 'Agenda', CalendarDays], ['address-book', 'Carnet d’adresses', BookUser], ['advertisements', 'Annonces', Megaphone],
      ['chat', 'Messages', MessageCircle], ['marketplace', 'Marketplace', Store], ['settings', 'Profil', Settings]
    ]
  },
  technician: {
    section: 'Technique',
    tabs: [
      ['overview', 'Suivi technique', Wrench], ['maintenance', 'Demandes d’intervention', Wrench], ['quotes', 'Devis', FileText],
      ['works', 'Travaux', Hammer], ['calendar', 'Calendrier & analyses', CalendarDays], ['visits', 'Visites et rondes', Clock], ['concierges', 'Concierges', Contact], ['cost-of-work', 'Coûts', Calculator], ['state-entry', 'États des lieux', ClipboardCheck],
      ['worker-contacts', 'Prestataires', Truck], ['preventive', 'Entretien préventif', CalendarDays],
      ['history', 'Historique technique', History], ['reports', 'Rapports techniques', FileSpreadsheet],
      ['agenda', 'Agenda', CalendarDays], ['advertisements', 'Annonces', Megaphone], ['chat', 'Messages', MessageCircle], ['marketplace', 'Marketplace', Store], ['settings', 'Profil', Settings]
    ]
  }
};

// Outils communs à tous les espaces : une seule entrée, prise dans le premier espace qui les propose
const SHARED = { agenda: 'agenda', 'address-book': 'address-book', 'documents-desk': 'documents-desk', advertisements: 'advertisements', messages: 'messages', chat: 'messages', settings: 'settings', subscription: 'subscription', backup: 'backup', reset: 'reset', marketplace: 'marketplace' };
const TOOLS_ORDER = ['agenda', 'address-book', 'documents-desk', 'advertisements', 'messages', 'marketplace', 'subscription', 'backup', 'reset', 'settings'];
const TOOL_LABELS = { agenda: 'Agenda', 'address-book': 'Carnet d’adresses', 'documents-desk': 'Documents', advertisements: 'Annonces', messages: 'Messages', subscription: 'Abonnement', backup: 'Sauvegarde', reset: 'Réinitialisation', marketplace: 'Marketplace', settings: 'Profil' };

// Ordre des rubriques
const ORDER = ['agency_director', 'admin', 'accounting', 'salesmanager', 'technician'];

/**
 * Menu combiné pour les rôles du compte : rubriques par fonction puis « Outils ». Le premier onglet de l'espace
 * principal devient « Tableau de bord ». La synthèse comptable de la direction disparaît quand la comptabilité
 * complète est présente.
 */
export function buildUnifiedMenu(roles) {
  const spaces = ORDER.filter((r) => roles.includes(r));
  const primary = roles.find((r) => SPACES[r]) || spaces[0];
  const sections = [];
  const tools = {};
  for (const space of spaces) {
    const items = [];
    for (const [tab, label, icon] of SPACES[space].tabs) {
      const shared = SHARED[tab];
      if (shared) {
        if (!tools[shared]) tools[shared] = { id: `${space}:${tab}`, space, tab, label: TOOL_LABELS[shared], icon };
        continue;
      }
      if (space === 'agency_director' && tab === 'accounting' && spaces.includes('accounting')) continue;
      const isHome = space === primary && tab === 'overview';
      items.push({ id: `${space}:${tab}`, space, tab, label: isHome ? 'Tableau de bord' : label, icon });
    }
    // Espace principal en tête, avec son tableau de bord en premier
    const entry = { key: space, label: SPACES[space].section, items };
    if (space === primary) sections.unshift(entry); else sections.push(entry);
  }
  const toolItems = TOOLS_ORDER.map((k) => tools[k]).filter(Boolean);
  if (toolItems.length) sections.push({ key: 'tools', label: 'Outils', items: toolItems });
  return sections;
}

/** Le compte cumule-t-il plusieurs fonctions (tableau de bord combiné) ? */
export function unifiedRolesOf(user) {
  const roles = Array.isArray(user?.roles) && user.roles.length ? user.roles : [user?.role];
  return roles.filter((r) => SPACES[r]);
}
